import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnInit,
  ViewChild,
  booleanAttribute,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPause, faVolumeMute, faVolumeUp, faPlay } from '@fortawesome/free-solid-svg-icons';
import { ButtonModule } from 'primeng/button';
import { Subject, take } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { UplayButtonComponent } from '@usheru-hff/core/uplay-button';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { MovieCompetitionService } from '@usheru-hff/sdk/movie';
import { AnyVideo, getMovieGenres, getMovieTags, Movie, TabType, ExtraRouteData } from '@usheru-hff/shared/domain';
import { LoadingIndicatorComponent } from '@usheru-hff/shared/ui-loading-indicator';
import { VideoComponent, VideoConsentService, VideoFullscreenService } from '@usheru-hff/shared/ui-video';
import { SharedUtilsModule } from '@usheru-hff/shared/utils';
import { SharedUtilsImageModule } from '@usheru-hff/shared/utils-image';
import { ShowtimeStreamingService } from '@usheru-hff/shared/utils-movie';
import {} from '@usheru-hff/shared/utils-navigation';
import { TRACKING_SERVICE } from '@usheru-hff/sdk/tracking';
import { MovieDetailSkeletonComponent, MovieLoadingComponent } from '@usheru-hff/ui/movie-detail-page';

import { LogoSizes } from './logo-sizes.interfaces';

@Component({
  selector: 'ush-feature-movie-hero',
  imports: [
    UplayButtonComponent,
    CommonModule,
    VideoComponent,
    ButtonModule,
    FontAwesomeModule,
    SharedUtilsImageModule,
    SharedUtilsModule,
    LoadingIndicatorComponent
  ],
  templateUrl: './feature-movie-hero.component.html',
  styleUrl: './feature-movie-hero.component.scss'
})
export class FeatureMovieHeroComponent implements OnInit, MovieLoadingComponent {
  private env = inject(ENVIRONMENT) as Environment;
  private route = inject(ActivatedRoute);
  private fullScreenService = inject(VideoFullscreenService);
  private videoConsentService = inject(VideoConsentService);
  private channelService = inject(ChannelService);
  private showtimesStreamingService = inject(ShowtimeStreamingService);
  private trackingService = inject(TRACKING_SERVICE);
  private movieCompetitionService = inject(MovieCompetitionService);
  private cd = inject(ChangeDetectorRef);

  readonly trackingCategory = 'MovieDetail';

  @ViewChild('video') videoComponent?: VideoComponent;

  @Input() disableTrailer = false;
  readonly movie = input<Movie | null>(null);
  @Input() disableMovieInfo = false;
  @Input() customMovieLogo: string | undefined;
  @Input() loading = false;
  @Input() set logoSizes(value: LogoSizes | undefined) {
    this.setLogoSizes(value);
  }
  @Input({ transform: booleanAttribute }) hideWatchNowCtaButton!: boolean;
  readonly isLoadingData = input(false);

  @HostBinding('attr.data-no-controls-available')
  get noControlsAvailable(): true | null {
    return !this.areControlsAvailable() ? true : null;
  }

  areControlsAvailable = computed(() => {
    if (!this.movie()) return false;

    return (
      this.showHeroButtons() ||
      this.hasTrailerSig() ||
      this.enableFullVideo() ||
      this.additionalLink() ||
      this.hasMoviePageCompetitionEnabled() ||
      this.hasUplayerSig() ||
      this.seeMoreEnabled()
    );
  });
  playing = signal<boolean>(false);
  seeMoreEnabled = signal<boolean>(this.env?.components?.movieDetailHero?.seeMoreEnabled ?? false);
  muteIcon = faVolumeUp;
  unmuteIcon = faVolumeMute;
  pauseIcon = faPause;
  playIcon = faPlay;

  embed = signal<boolean>(false);
  autoplay = signal<boolean>(false);
  muted = signal<boolean>(false);
  embedVideoControls = signal<boolean>(false);

  titleSig = computed(() => this.movie()?.title);
  releaseMessageSig = computed(() => this.movie()?.releaseMessage);
  hasTrailerSig = computed(() => !!this.movie()?.trailerLink);
  heroImageSig = computed(() => this.getHeroImageFromMovie(this.movie()));
  hasUplayerSig = computed(() => !!this.movie()?.hasPlayer);

  /**
   * Controls the hero competition CTA visibility.
   *
   * The button should only show when the movie has competition configuration
   * and the matching `ush-movie-competition` component has actually rendered.
   * `MovieCompetitionService` exposes that render state reactively, so this
   * computed updates when the competition component is created or destroyed.
   */
  hasMoviePageCompetitionEnabled = computed(() => {
    const movie = this.movie();

    return (
      movie?.properties?.competition?.typeFormEnabled === true &&
      this.movieCompetitionService.isCompetitionComponentActive(movie?.friendlyUrl || '')
    );
  });

  showHeroButtons = computed(
    () =>
      this.ctaButtonLabel ||
      (!this.disableTrailer && this.hasTrailerSig()) ||
      this.enableFullVideo() ||
      this.additionalLink() ||
      this.hasMoviePageCompetitionEnabled() ||
      this.hasUplayerSig() ||
      this.seeMoreEnabled()
  );
  openTab!: TabType;

  /**
   * This is required because the logo is rendered one and the sizes could be set as an input
   */
  // logoSizesReady = signal(false);
  readonly logoSizesDefault: LogoSizes = {
    width: 450,
    height: 150,
    sizes: '(max-width:530px) 90vw, (max-width:1000px) 50vw, 30vw',
    style: 'height: auto; max-width: 50vw;'
  };

  _logoSizes: LogoSizes | undefined;
  get logoSizes(): LogoSizes | undefined {
    return this._logoSizes;
  }

  //

  enableFullVideo = signal(false);
  additionalLink = signal('');

  destroy$ = new Subject();

  constructor() {
    this.setLogoSizes();

    // If the movie changes for no reason multiple times, that will cause performance issues.
    effect(
      () => {
        const movie = this.movie();
        if (movie) {
          this.setUpVariables(movie);
        }
      },
      {
        allowSignalWrites: true
      }
    );
  }

  ngOnInit(): void {
    this.checkEnableFullVideo();
    this.checkAdditionalLink();
  }

  setUpVariables(movie: Movie) {
    this.embed.set(this.isEmbedEnabled());
    this.autoplay.set(this.isAutoplayEnabled());
    this.embedVideoControls.set(this.autoplay() && this.embed());
    this.setUpCtaMessage(movie);
  }

  getHeroImageFromMovie(movie: Movie | null | undefined): string {
    if (!movie) return '';
    return movie.tilePhotoUrl || movie.fullpageImageUrl || this.placeholderImg || '';
  }

  private setUpCtaMessage(movie: Movie): void {
    this.showtimesStreamingService
      .determineShowtimeStreamingActiveType(movie)
      .pipe(take(1))
      .subscribe(type => {
        this.openTab = this.showtimesStreamingService.getTabType(type);
        this.cd.markForCheck();
      });
  }

  private checkEnableFullVideo() {
    const fullVideoParam = (this.route.snapshot.data as ExtraRouteData).fullVideo;
    if (this.env.movieDetail?.hero?.fullVideo && fullVideoParam) {
      this.enableFullVideo.set(true);
    }
  }

  private checkAdditionalLink() {
    const additionalLink = (this.route.snapshot.data as ExtraRouteData).additionalLink;
    this.additionalLink.set(additionalLink || '');
  }

  private isEmbedEnabled(): boolean {
    return this.env?.movieDetail?.hero?.embed || this.env.customPageParams?.movieDetailVideoEmbed || false;
  }
  private isAutoplayEnabled() {
    return this.env.movieDetail?.hero?.autoplay || false;
  }

  get placeholderImg(): string {
    return this.channelService.getDefaultImg('landscape');
  }

  get logoUrl(): string | undefined {
    return this.customMovieLogo || this.movie()?.logoUrl;
  }

  get logoSkeletonWidth(): string {
    const width = this.logoSizes?.width || this.logoSizesDefault.width!;

    return `min(90vw, ${(width / 3) * 2}px)`;
  }

  get logoSkeletonHeight(): string {
    const { width: defaultWidth, height: defaultHeight } = this.logoSizesDefault;
    const width = this.logoSizes?.width || defaultWidth!;
    const height = this.logoSizes?.height || defaultHeight!;

    return `min(calc(90vw * ${height / width}), ${(height / 3) * 2}px)`;
  }

  get ctaButtonLabel(): string {
    if (this.seeMoreEnabled()) return $localize`:@@movie.cta.all:All`;
    if (!this.openTab || this.hideWatchNowCtaButton) return '';

    return this.openTab === 'in-cinemas'
      ? $localize`:@@movie.cta.inCinemas:Book now`
      : $localize`:@@movie.cta.watchAtHome:Watch now`;
  }

  get competitionButtonLabel(): string {
    return $localize`:@@movie.cta.enterNowToWin:Enter now to win`;
  }

  onPlayTrailerClick(): void {
    this.playVideo();
  }

  onPlayFullVideoClick(): void {
    this.playFullscreenVideo('fullvideo');
    const movie = this.movie();
    if (!movie) return;
    this.trackingService.trackEvent({
      action: 'FullVideo',
      properties: {
        category: 'MovieDetail',
        movie: movie.title || '',
        idMovie: movie.id,
        imdb: movie?.imdb,
        tagsMovie: getMovieTags(movie),
        genresMovie: getMovieGenres(movie)
      }
    });
  }

  onPauseButtonClick() {
    this.videoComponent?.pause();
    this.embedVideoControls.set(false);
  }

  muteVideo(): void {
    this.videoComponent?.setMute();
    this.muted.set(true);
  }

  unmuteVideo(): void {
    this.videoComponent?.setUnmute();
    this.muted.set(false);
  }

  onCtaClick(): void {
    if (this.seeMoreEnabled()) {
      this.handleSeeMore();
      return;
    }

    if (this.openTab) {
      this.scrollToElement('showtimeStreamingWidget');
    }
  }

  onCompetitionClick(): void {
    this.scrollToElement('competition');
  }

  private scrollToElement(id: string): void {
    const el = document.getElementById(id);
    if (!el) return;

    const offset = 100;
    const elementPosition = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: elementPosition, behavior: 'smooth' });
  }
  private handleSeeMore(): void {
    const el = document.getElementById('movieDetailPoint');
    if (el) {
      const offset = 100;
      const elementPosition = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: elementPosition, behavior: 'smooth' });
    }
  }

  private playVideo(): void {
    if (this.embed()) {
      this.playEmbedVideo();
    } else {
      this.playFullscreenVideo();
    }
  }

  private playEmbedVideo(): void {
    const movie = this.movie();
    if (!movie) return;

    this.videoConsentService.ensureConsentForContent(
      movie,
      {
        requireConsentFor: ['youtube', 'vimeo', 'dailymotion']
      },
      () => {
        this.playing.set(true);
        this.embedVideoControls.set(true);
        if (this.videoComponent) {
          this.videoComponent?.reanude();
        } else {
          const videoReadyInterval = setInterval(() => {
            if (this.videoComponent) {
              this.videoComponent?.reanude();
              clearInterval(videoReadyInterval);
            }
          }, 200);
        }
      }
    );
  }

  onEmbedVideoReady(): void {
    if (this.playing()) {
      this.playEmbedVideo();
    }
  }

  private playFullscreenVideo(type: 'trailer' | 'fullvideo' = 'trailer'): void {
    const movie = this.movie();
    if (!movie) return;

    if (type === 'trailer' && movie.trailerLink) {
      this.fullScreenService.playWithConsent({
        content: movie,
        category: this.trackingCategory,
        animate: true,
        videoSettings: { muted: false, autoplay: true }
      });
    }
    if (type === 'fullvideo' && movie.fullVideoUrl) {
      this.fullScreenService.playWithConsent({
        content: {
          title: movie.title,
          url: movie.fullVideoUrl
        } as AnyVideo,
        category: this.trackingCategory,
        animate: true,
        videoSettings: { muted: false, autoplay: true }
      });
    }
  }

  private setLogoSizes(value?: LogoSizes): void {
    const { width, height, sizes, style } = value || {};

    this._logoSizes = {
      width: width || this.logoSizesDefault.width,
      height: height || this.logoSizesDefault.height,
      sizes: sizes || this.logoSizesDefault.sizes,
      style: style || this.logoSizesDefault.style
    };
  }
}
